import { computed } from 'vue'
import { useAssetForm, type UseAssetForm } from '..'
import type { UseAssetFormLayout } from '../types'
import { ref } from 'vue'
import { clamp } from 'math'
import {
  useFormWizard,
  type UseFormWizardReturn
} from 'components/form-wizard/hooks/useFormWizard'
import type { FormWizardStep } from 'components/form-wizard/types'
import { EAssetType } from 'findef'
import { WizardWrapper } from '../components/wizardWrapper'
import { AssetInfoBanner } from '@/modules/asset/components/asset-info-banner'



import { step1Layout } from '../layouts/steps/step1'
import { step2Layout } from '../layouts/steps/step2'
import { Form } from '@/form/useForm'
import type { Ref } from 'vue'
import { AddAssetSaveLoader } from '../components/addAssetSaveLoader'
import type { IInputOption } from 'components/form/input/types'

export type UseAddAsset = {
  manage: UseAssetForm
  wizard: UseFormWizardReturn;
  setAssetType: (assetType: EAssetType, listed?: boolean) => void;
  stepIndex: Ref<number>;
  isReady: Ref<boolean>;
  __v_skip: true;
}

export type UseAddAssetProps = {
  onCancelOverride?: () => any;
  onSave?: () => any;
  onGoBack?: (index: number) => void;
  onLastGoBack?: (index: number) => void;
  redirectOnSave?: boolean;
  importAssetsModalOptions?: any;
}

export const useAddAsset = (props: UseAddAssetProps = {}): UseAddAsset => {
  const router = useRouter()
  const isReady = ref<boolean>(false);

  const stepIndex = ref<number>(0);
  const availLayouts: UseAssetFormLayout[] = [
    step1Layout,
    step2Layout
  ]

  const currentLayout = computed(() => {
    return availLayouts[clamp(stepIndex.value, 0, availLayouts.length - 1)]
  })

  const manage = useAssetForm({
    layout: currentLayout
  });

  const setAssetType = (assetType: EAssetType, listed?: boolean) => {
    isReady.value = true;

    console.log(`setAssetType(${assetType})`);
    console.log('Setting listed to: ', listed);
    manage.form.resetState()
    manage.form.setFieldValue('asset.type', assetType)
    manage.form.setFieldValue('asset.listed', !!listed)
  }

  const ownerLabel = computed(() => {
	const field = manage.form.getField('parentId')
	const value = field.value.value
	if (typeof value === 'undefined') return ''

	const options = unref(field.attributes.value.options?.computedItems) ?? field.attributes.value.options?.items
	return options?.find((option: IInputOption) => option.value === value)?.label ?? ''
  })

  const infoBanner = computed(() => ({
    component: () => <AssetInfoBanner formState={manage.form.state.value} ownerLabel={ownerLabel.value}/>
  }));

  const steps = computed((): FormWizardStep[] => {
    return [
      {
        title: 'Asset Information',
        body: () => (
          <WizardWrapper stepIndex={stepIndex}>
            <Form form={manage.form} />
          </WizardWrapper>
        )
      },
      {
        title: 'Investment',
        body: () => (
          <WizardWrapper
            banner={infoBanner.value}
            stepIndex={stepIndex}
            isLoading={manage.isSaving}
            progress={manage.loadingProgress}
            loadingState={() => {
              return (
                <div class="w-full h-full flex items-center justify-center bg-gray-50 absolute top-0 left-0">
                  <AddAssetSaveLoader
                    progress={manage.loadingProgress}
                    form={manage.form}
                  />
                </div>
              )
            }}
          >
            <Form form={manage.form} />
          </WizardWrapper>
        )
      }
    ]
  });

  const wizard = useFormWizard({
    steps: steps,
    hideActions: manage.isSaving,
    onIndexChange: (index) => {
      // if (isEditMode.value) return

      stepIndex.value = Math.max(0, index)
      //  childIndex.value = clamp(index - 1, 0, 1)
    },
    onSave: async () => {
      await manage.save();

      if (props.onSave) {
        props.onSave();
      }
      if (props.redirectOnSave !== false) {
        await router.push('/portfolio')
      }
      // await save() //await save()
    },
    onGoBack: (index) => {
      if (props.onGoBack) {
        props.onGoBack(index);
      }
    },
    onLastGoBack: (index) => {
      manage.form.resetState();
      isReady.value = false;

      if (props.onLastGoBack) {
        props.onLastGoBack(index);
      }
    },
    globalTitle: 'Add Asset',
    onCancel: async () => {
      if (props.onCancelOverride) {
        props.onCancelOverride();
      } else {
    //    await router.push('/portfolio')
      }
    },
    nextButtonText: computed(() => (stepIndex.value >= availLayouts.length - 1 ? 'Save' : 'Next')),
    showFooter: computed(() => stepIndex.value >= 0),
    errors: computed(() => manage.form.errors.value.map((err) => err.message)),
    nextButtonDisabled: computed(() => {
      const field = manage.form.getField('ownershipPercentage')
      return (field?.attributes?.value?.errors?.length || 0) > 0
    })
  });


  return { manage, wizard, setAssetType, isReady, stepIndex, __v_skip: true }
}
